/* ==========================================================================
   CUANTUM · JS compartido de las secciones cq-*
   Sin dependencias. Usa custom elements, así funciona también al recargar
   secciones dentro del editor de temas de Shopify.
   ========================================================================== */
(() => {
  if (window.__cqLoaded) return;
  window.__cqLoaded = true;

  // Formatea con el mismo formato de moneda que la tienda (shop.money_format), p. ej. "€{{amount_with_comma_separator}}".
  const money = (cents, currency, format) => {
    if (format) {
      const n = (Number(cents) || 0) / 100;
      const sep = (num, dec, th, ds) => {
        const [i, d] = num.toFixed(dec).split('.');
        return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? ds + d : '');
      };
      const map = {
        amount: sep(n, 2, ',', '.'),
        amount_no_decimals: sep(n, 0, ',', '.'),
        amount_with_comma_separator: sep(n, 2, '.', ','),
        amount_no_decimals_with_comma_separator: sep(n, 0, '.', ','),
        amount_with_space_separator: sep(n, 2, ' ', ','),
        amount_with_apostrophe_separator: sep(n, 2, "'", '.'),
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (k in map ? map[k] : m));
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', { style: 'currency', currency: currency || 'EUR' }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
  };

  /* ---------- Sin animaciones (v4, estilo Pleto) ----------
     Las clases .cq-reveal, .cq-reveal-img y .cq-split-words se mantienen en el HTML
     por compatibilidad, pero se marcan como visibles al instante: no hay efectos
     de aparición, ni palabras animadas, ni contadores. */
  function scan(root = document) {
    root.querySelectorAll('.cq-reveal, .cq-reveal-img, .cq-split-words').forEach((el) => el.classList.add('is-in'));
  }

  /* ---------- Tabs ---------- */
  class CqTabs extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => this.select(i));
        btn.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.buttons.length) % this.buttons.length;
          this.select(next);
          this.buttons[next].focus();
        });
      });
    }
    select(index) {
      this.buttons.forEach((b, i) => {
        b.setAttribute('aria-selected', i === index ? 'true' : 'false');
        b.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((p, i) => { p.hidden = i !== index; });
    }
  }

  /* ---------- Antes / después ---------- */
  class CqCompare extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      const box = this.querySelector('.cq-ba');
      if (!range || !box) return;
      const set = () => box.style.setProperty('--pos', range.value + '%');
      range.addEventListener('input', set);
      set();
    }
  }

  /* ---------- Galería de producto ---------- */
  class CqGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.cq-gallery__track');
      this.slides = [...this.querySelectorAll('.cq-gallery__slide')];
      this.thumbs = [...this.querySelectorAll('.cq-gallery__thumb')];
      this.counter = this.querySelector('[data-cq-counter]');
      if (!this.track) return;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      this.querySelector('.cq-gallery__nav--prev')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('.cq-gallery__nav--next')?.addEventListener('click', () => this.go(this.index + 1));
      this.index = 0;
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.update(i);
        });
      }, { passive: true });
    }
    go(i) {
      const n = this.slides.length;
      const idx = (i + n) % n;
      this.track.scrollTo({ left: idx * this.track.clientWidth, behavior: 'auto' });
      this.update(idx);
    }
    update(i) {
      this.index = i;
      this.thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
    }
  }

  /* ---------- Buy box: opciones de compra ---------- */
  class CqBuyBox extends HTMLElement {
    connectedCallback() {
      this.currency = this.dataset.currency || 'EUR';
      this.format = this.dataset.moneyFormat || '';
      this.form = this.querySelector('form');
      this.inputQty = this.querySelector('input[name="quantity"]');
      this.inputPlan = this.querySelector('input[name="selling_plan"]');
      this.ctaPrice = this.querySelectorAll('[data-cq-cta-price]');
      this.radios = [...this.querySelectorAll('input[type="radio"][data-cq-option]')];
      this.radios.forEach((r) => r.addEventListener('change', () => this.apply()));
      this.querySelectorAll('.cq-pack').forEach((pack) => {
        pack.addEventListener('click', (e) => {
          e.preventDefault();
          const opt = pack.closest('.cq-po__opt');
          opt.querySelectorAll('.cq-pack').forEach((p) => p.setAttribute('aria-pressed', p === pack ? 'true' : 'false'));
          const radio = opt.querySelector('input[type="radio"]');
          Object.assign(radio.dataset, {
            qty: pack.dataset.qty,
            total: pack.dataset.total,
            compare: pack.dataset.compare || '',
            meta: pack.dataset.meta || ''
          });
          radio.checked = true;
          this.apply();
        });
      });
      this.form?.addEventListener('submit', (e) => {
        const btn = this.querySelector('button[type="submit"]');
        const cartEl = document.querySelector('cq-cart');
        // "Ir directo al pago" (return_to=/checkout) o sin carrito lateral: envío normal.
        if (!cartEl || this.form.querySelector('input[name="return_to"]')) { btn?.classList.add('is-loading'); return; }
        e.preventDefault();
        btn?.classList.add('is-loading');
        fetch(cartEl.route('cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(this.form) })
          .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
          .then(({ ok, j }) => {
            if (!ok) throw new Error(j.description || j.message || 'add');
            return cartEl.open();
          })
          .catch(() => { HTMLFormElement.prototype.submit.call(this.form); })
          .finally(() => btn?.classList.remove('is-loading'));
      });
      this.apply();
    }
    apply() {
      const r = this.radios.find((x) => x.checked) || this.radios[0];
      if (!r) return;
      this.radios.forEach((x) => x.closest('.cq-po__opt')?.classList.toggle('is-active', x === r));
      if (this.inputQty) this.inputQty.value = r.dataset.qty || 1;
      if (this.inputPlan) {
        this.inputPlan.value = r.dataset.plan || '';
        this.inputPlan.disabled = !r.dataset.plan;
      }
      const opt = r.closest('.cq-po__opt');
      const priceEl = opt?.querySelector('[data-cq-price]');
      const compareEl = opt?.querySelector('[data-cq-compare]');
      const metaEl = opt?.querySelector('[data-cq-meta]');
      const total = parseInt(r.dataset.total || '0', 10);
      const compare = parseInt(r.dataset.compare || '0', 10);
      if (priceEl && total) priceEl.textContent = money(total, this.currency, this.format);
      if (compareEl) {
        compareEl.textContent = compare > total ? money(compare, this.currency, this.format) : '';
        compareEl.hidden = !(compare > total);
      }
      if (metaEl && r.dataset.meta) metaEl.textContent = r.dataset.meta;
      this.ctaPrice.forEach((el) => { el.textContent = total ? money(total, this.currency, this.format) : ''; });
      document.dispatchEvent(new CustomEvent('cq:option-change', { detail: { total, label: r.dataset.label || '' } }));
    }
  }

  /* ---------- Fecha estimada de entrega ---------- */
  class CqDelivery extends HTMLElement {
    connectedCallback() {
      this.cutoff = parseInt(this.dataset.cutoff || '14', 10);
      this.min = parseInt(this.dataset.min || '1', 10);
      this.max = parseInt(this.dataset.max || '2', 10);
      this.holidays = (this.dataset.holidays || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.tpl = this.querySelector('[data-cq-delivery-text]');
      if (!this.tpl) return;
      this.render();
      this.timer = setInterval(() => this.render(), 60000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    isWorkday(d) {
      const pad = (n) => String(n).padStart(2, '0');
      const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      return d.getDay() !== 0 && d.getDay() !== 6 && !this.holidays.includes(iso);
    }
    addWorkdays(date, n) {
      const d = new Date(date);
      let added = 0;
      while (added < n) {
        d.setDate(d.getDate() + 1);
        if (this.isWorkday(d)) added++;
      }
      return d;
    }
    render() {
      const now = new Date();
      let ship = new Date(now);
      const beforeCutoff = this.isWorkday(now) && now.getHours() < this.cutoff;
      if (!beforeCutoff) {
        ship = this.addWorkdays(now, 1);
      }
      const from = this.addWorkdays(ship, this.min);
      const to = this.addWorkdays(ship, this.max);
      const lang = document.documentElement.lang || 'es-ES';
      const f = (d) => new Intl.DateTimeFormat(lang, { weekday: 'long', day: 'numeric', month: 'long' }).format(d);
      const fill = (tpl, map) => Object.keys(map).reduce((t, k) => t.split('{' + k + '}').join(map[k]), tpl);
      const tplRange = this.dataset.tplRange || 'entre el {from} y el {to}';
      const tplSingle = this.dataset.tplSingle || 'el {date}';
      const when = this.min === this.max ? fill(tplSingle, { date: f(from) }) : fill(tplRange, { from: f(from), to: f(to) });
      let countdown = '';
      if (beforeCutoff) {
        const cut = new Date(now); cut.setHours(this.cutoff, 0, 0, 0);
        const mins = Math.max(0, Math.round((cut - now) / 60000));
        const h = Math.floor(mins / 60); const m = mins % 60;
        countdown = `${h > 0 ? h + ' h ' : ''}${m} min`;
      }
      const tplToday = this.dataset.tplToday || 'Pide en las próximas {time} y recíbelo <strong>{when}</strong>.';
      const tplLater = this.dataset.tplLater || 'Pide hoy y recíbelo <strong>{when}</strong>.';
      this.tpl.innerHTML = beforeCutoff
        ? fill(tplToday, { time: `<span class="cq-delivery__timer">${countdown}</span>`, when })
        : fill(tplLater, { when });
    }
  }

  /* ---------- Barra fija de compra ---------- */
  class CqSticky extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector(this.dataset.watch || '[data-cq-main-cta]');
      const bar = this.querySelector('.cq-sticky');
      if (!target || !bar || !('IntersectionObserver' in window)) return;
      this.obs = new IntersectionObserver(([e]) => {
        const passed = e.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', !e.isIntersecting && passed);
      });
      this.obs.observe(target);
      bar.querySelector('[data-cq-sticky-buy]')?.addEventListener('click', (e) => {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'auto', block: 'center' });
      });
      document.addEventListener('cq:option-change', (ev) => {
        const p = bar.querySelector('[data-cq-sticky-price]');
        if (p && ev.detail.total) p.textContent = money(ev.detail.total, this.dataset.currency || 'EUR', this.dataset.moneyFormat || '');
      });
    }
    disconnectedCallback() { this.obs?.disconnect(); }
  }

  /* ---------- Índice del artículo + barra de progreso ---------- */
  class CqToc extends HTMLElement {
    connectedCallback() {
      const content = document.querySelector(this.dataset.content || '.cq-rte');
      const list = this.querySelector('ol');
      if (!content || !list) return;
      const heads = [...content.querySelectorAll('h2')];
      if (!heads.length) { this.hidden = true; return; }
      heads.forEach((h, i) => {
        if (!h.id) h.id = 'cq-sec-' + (i + 1);
        const li = document.createElement('li');
        li.innerHTML = `<a href="#${h.id}">${h.textContent}</a>`;
        list.appendChild(li);
      });
      const links = [...list.querySelectorAll('a')];
      if ('IntersectionObserver' in window) {
        const obs = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) links.forEach((a) => a.classList.toggle('is-active', a.getAttribute('href') === '#' + en.target.id));
          });
        }, { rootMargin: '0px 0px -70% 0px' });
        heads.forEach((h) => obs.observe(h));
      }
      const bar = document.querySelector('.cq-article__progress');
      if (bar) {
        const onScroll = () => {
          const r = content.getBoundingClientRect();
          const p = Math.min(Math.max(-r.top / (r.height - window.innerHeight), 0), 1);
          bar.style.width = (p * 100) + '%';
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    }
  }

  /* ---------- Diálogos ("Qué significa esto") ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cq-open]');
    if (opener) {
      const dlg = document.getElementById(opener.dataset.cqOpen);
      if (dlg?.showModal) { e.preventDefault(); dlg.showModal(); }
      return;
    }
    const closer = e.target.closest('[data-cq-close]');
    if (closer) closer.closest('dialog')?.close();
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('cq-dialog')) e.target.close();
  });

  /* ---------- Cabecera: menú móvil y carrito ---------- */
  class CqHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.cq-hdr__drawer');
      this.querySelectorAll('[data-cq-menu-open]').forEach((b) => b.addEventListener('click', () => {
        if (this.drawer && typeof this.drawer.showModal === 'function') {
          this.drawer.showModal();
          document.documentElement.classList.add('cq-lock');
        }
      }));
      this.querySelectorAll('[data-cq-menu-close]').forEach((b) => b.addEventListener('click', () => this.drawer && this.drawer.close()));
      if (this.drawer) this.drawer.addEventListener('click', (e) => { if (e.target === this.drawer) this.drawer.close(); });
      // El carrito abre el carrito lateral; si no existe, va a /cart.
      this.querySelectorAll('[data-cq-cart]').forEach((a) => a.addEventListener('click', (e) => {
        const cartEl = document.querySelector('cq-cart');
        if (!cartEl) return;
        e.preventDefault();
        if (this.drawer?.open) this.drawer.close();
        cartEl.open();
      }));
      if (this.drawer) {
        this.drawer.addEventListener('close', () => document.documentElement.classList.remove('cq-lock'));
      }
    }
  }

  /* ---------- Carrito lateral (estilo Pleto) ---------- */
  const ICON = {
    minus: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M6 12h12"/></svg>',
    plus: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M12 6v12M6 12h12"/></svg>'
  };
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  class CqCart extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-cq-cart-body]');
      this.foot = this.querySelector('[data-cq-cart-foot]');
      this.subtotal = this.querySelector('[data-cq-cart-subtotal]');
      this.error = this.querySelector('[data-cq-cart-error]');
      this.querySelector('[data-cq-cart-close]')?.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      this.dialog?.addEventListener('close', () => document.documentElement.classList.remove('cq-lock'));
      this.body?.addEventListener('click', (e) => {
        const item = e.target.closest('[data-key]');
        if (!item) return;
        const qtyBtn = e.target.closest('[data-cq-qty]');
        if (qtyBtn) this.change(item.dataset.key, Math.max(0, Number(item.dataset.qty) + Number(qtyBtn.dataset.cqQty)));
        if (e.target.closest('[data-cq-remove]')) this.change(item.dataset.key, 0);
      });
    }
    route(path) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return root + path;
    }
    m(cents) { return money(cents, this.dataset.currency, this.dataset.moneyFormat); }
    open() {
      if (!this.dialog) return Promise.resolve();
      if (!this.dialog.open) {
        this.dialog.showModal();
        document.documentElement.classList.add('cq-lock');
      }
      return this.refresh();
    }
    close() { this.dialog?.close(); }
    refresh() {
      return fetch(this.route('cart.js'), { headers: { Accept: 'application/json' } })
        .then((r) => r.json()).then((cart) => this.render(cart)).catch(() => this.showError());
    }
    change(key, quantity) {
      this.classList.add('is-busy');
      this.dialog?.classList.add('is-busy');
      return fetch(this.route('cart/change.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      }).then((r) => { if (!r.ok) throw new Error('change'); return r.json(); })
        .then((cart) => this.render(cart))
        .catch(() => { this.showError(); return this.refresh(); })
        .finally(() => { this.classList.remove('is-busy'); this.dialog?.classList.remove('is-busy'); });
    }
    showError() {
      if (!this.error) return;
      this.error.textContent = this.dataset.tError || '';
      this.error.hidden = false;
      setTimeout(() => { this.error.hidden = true; }, 4000);
    }
    img(url) {
      if (!url) return '';
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=300';
    }
    render(cart) {
      const n = cart.item_count || 0;
      document.querySelectorAll('[data-cq-cart-count]').forEach((el) => { el.textContent = n; el.hidden = n === 0; });
      document.querySelectorAll('[data-cq-cart-count-text]').forEach((el) => { el.textContent = n; });
      if (!this.body) return;
      if (!n) {
        this.body.innerHTML = '<div class="cq-cart__empty"><p>' + esc(this.dataset.tEmpty) + '</p><a class="cq-btn" href="' + esc(this.dataset.shopUrl) + '">' + esc(this.dataset.tContinue) + '</a></div>';
        if (this.foot) this.foot.hidden = true;
        return;
      }
      if (this.foot) this.foot.hidden = false;
      this.body.innerHTML = cart.items.map((it) => {
        const plan = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan ? it.selling_plan_allocation.selling_plan.name : '';
        const variant = it.variant_title && !/default/i.test(it.variant_title) ? it.variant_title : '';
        const sub = [variant, plan].filter(Boolean).join(' · ');
        const alloc = it.selling_plan_allocation;
        const planCompare = alloc && alloc.compare_at_price ? alloc.compare_at_price * it.quantity : 0;
        const before = Math.max(it.original_line_price || 0, planCompare);
        const compare = before > it.final_line_price ? '<s>' + this.m(before) + '</s>' : '';
        return '<div class="cq-cart__item" data-key="' + esc(it.key) + '" data-qty="' + it.quantity + '">' +
          '<a class="cq-cart__img" href="' + esc(it.url) + '">' + (it.image ? '<img src="' + esc(this.img(it.image)) + '" alt="' + esc(it.product_title) + '" width="140" height="140" loading="eager">' : '') + '</a>' +
          '<div class="cq-cart__info">' +
            '<div class="cq-cart__row1"><a class="cq-cart__name" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' +
            '<span class="cq-cart__price">' + compare + '<b>' + this.m(it.final_line_price) + '</b></span></div>' +
            (sub ? '<p class="cq-cart__plan">' + esc(sub) + '</p>' : '') +
            '<div class="cq-cart__row2"><div class="cq-cart__qty">' +
              '<button type="button" data-cq-qty="-1" aria-label="' + esc(this.dataset.tDecrease) + '">' + ICON.minus + '</button>' +
              '<span>' + it.quantity + '</span>' +
              '<button type="button" data-cq-qty="1" aria-label="' + esc(this.dataset.tIncrease) + '">' + ICON.plus + '</button>' +
            '</div><button type="button" class="cq-cart__remove" data-cq-remove>' + esc(this.dataset.tRemove) + '</button></div>' +
          '</div></div>';
      }).join('');
      if (this.subtotal) this.subtotal.textContent = this.m(cart.total_price);
    }
  }

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  define('cq-tabs', CqTabs);
  define('cq-compare', CqCompare);
  define('cq-gallery', CqGallery);
  define('cq-buy-box', CqBuyBox);
  define('cq-delivery', CqDelivery);
  define('cq-sticky', CqSticky);
  define('cq-header', CqHeader);
  define('cq-cart', CqCart);
  define('cq-toc', CqToc);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
})();
